import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { JobDictionaryDialogComponent } from 'src/app/modals/job-dictionary-dialog/job-dictionary-dialog.component';
import { JobMappingSourceType } from 'src/app/enums/jobMappingSourceType.enum';
import {
  BulkAssignMappingRequest,
  BulkAssignOutcome,
  BulkAssignResultItem,
  JobDictionaryMapping,
  JobMappingCandidate
} from 'src/app/models/jobDictionaryMapping.model';
import { JobDictionary } from 'src/app/models/jobDictionary.model';
import { JobDictionaryMappingService } from 'src/app/services/job-dictionary-mapping.service';
import { JobDictionaryService } from 'src/app/services/job-dictionary.service';
import { PermissionService } from 'src/app/services/permission.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { ConfirmDialogComponent } from 'src/app/shared/confirmDialog/confirmDialog.component';
import { GridPermissions, gridToPermissionFlags } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import {
  workforceRouteConfig,
  WorkforceTabLink
} from 'src/app/shared/constants/workforceRouteConfig.constants';
import { WorkforceTabsService } from 'src/app/services/workforce-tabs.service';

interface CandidateGroup {
  sourceType: JobMappingSourceType;
  label: string;
  items: JobMappingCandidate[];
}

interface MappedGroup {
  sourceType: JobMappingSourceType;
  label: string;
  items: JobDictionaryMapping[];
}

type ViewMode = 'unmapped' | 'mapped';

/** Mirrors the BE BulkAssignMappingRequest.Candidates [MaxLength(1000)] cap. */
const MAX_BULK_ASSIGN_CANDIDATES = 1000;

/** Full-list fetch page size for the mapping workspace (groupings + mappings are bounded datasets). */
const WORKSPACE_PAGE_SIZE = 10000;

/**
 * First-page index for the workspace list fetches. The BE grid pages with
 * Skip(page * pageSize) (0-based), so the first page MUST be 0 — passing 1 with a
 * large page size skips the entire result set and returns an empty list (DASH-2692).
 */
const WORKSPACE_PAGE = 0;

@Component({
  standalone: false,
  selector: 'app-job-mapping',
  templateUrl: './job-mapping.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./job-mapping.component.scss']
})
export class JobMappingComponent implements OnInit, OnDestroy {

  viewMode: ViewMode = 'unmapped';

  candidates: JobMappingCandidate[] = [];
  mappings: JobDictionaryMapping[] = [];
  groupings: JobDictionary[] = [];

  // Pre-computed view models (refreshed on data/search change) so the template
  // never calls filtering/grouping getters during change detection.
  candidateGroupsView: CandidateGroup[] = [];
  mappedGroupsView: MappedGroup[] = [];
  filteredGroupingsView: JobDictionary[] = [];
  private mappingCountMap = new Map<string, number>();

  candidatesLoading = false;
  candidatesError = false;
  mappingsLoading = false;
  mappingsError = false;
  groupingsLoading = false;
  groupingsError = false;

  leftSearch = '';
  rightSearch = '';

  private selection$ = new BehaviorSubject<Set<string>>(new Set<string>());
  selectedKeys = new Set<string>();

  private destroy$ = new Subject<void>();

  readonly JobMappingSourceType = JobMappingSourceType;

  // Workforce tab bar links so Job Mapping renders as a contained tab panel
  // alongside its sibling tabs (Workforce, Shifts, Job Dictionary), matching the
  // other grid-based tabs. Starts with the static config as a fallback; the
  // dynamic Workforce employee-table tab is resolved and prepended in ngOnInit
  // (DASH-2723). app-tab-main also re-derives the active index from the URL, but
  // we recompute selectedTabIndex after the async prepend to avoid a flash and
  // keep the highlight correct on hard reload / direct URL entry.
  tabs: WorkforceTabLink[] = [...workforceRouteConfig];
  selectedTabIndex = this.getActiveTabIndex(this.tabs);

  constructor(
    private mappingService: JobDictionaryMappingService,
    private jobDictionaryService: JobDictionaryService,
    private dialog: MatDialog,
    private toastService: ToastService,
    private translateService: TranslateService,
    private permissionService: PermissionService,
    private workforceTabsService: WorkforceTabsService
  ) { }

  private getActiveTabIndex(tabs: WorkforceTabLink[]): number {
    return tabs.findIndex(
      (link) => link.objectId === RolePermissionObjects.JobDictionaryMapping
    );
  }

  get canWrite(): boolean {
    return this.permissionService.hasPermissionFlag(
      RolePermissionObjects.JobDictionaryMapping,
      gridToPermissionFlags[GridPermissions.Create]
    );
  }

  ngOnInit(): void {
    this.selection$
      .pipe(takeUntil(this.destroy$))
      .subscribe((keys) => (this.selectedKeys = keys));
    this.workforceTabsService.getWorkforceTabs()
      .pipe(takeUntil(this.destroy$))
      .subscribe((tabs) => {
        this.tabs = tabs;
        this.selectedTabIndex = this.getActiveTabIndex(tabs);
      });
    this.loadCandidates();
    this.loadMappings();
    this.loadGroupings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCandidates(): void {
    this.candidatesLoading = true;
    this.candidatesError = false;
    this.mappingService.getUnmappedCandidates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (candidates) => {
          this.candidates = [...candidates].sort((a, b) => b.occurrenceCount - a.occurrenceCount);
          this.candidateGroupsView = this.buildCandidateGroups();
          this.candidatesLoading = false;
        },
        error: () => {
          this.candidatesError = true;
          this.candidatesLoading = false;
        }
      });
  }

  loadMappings(): void {
    this.mappingsLoading = true;
    this.mappingsError = false;
    this.mappingService.get('', 'sourceValue', 'asc', WORKSPACE_PAGE, WORKSPACE_PAGE_SIZE, '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.mappings = response?.data ?? [];
          this.buildMappingCountMap();
          this.mappedGroupsView = this.buildMappedGroups();
          this.mappingsLoading = false;
        },
        error: () => {
          this.mappingsError = true;
          this.mappingsLoading = false;
        }
      });
  }

  loadGroupings(): void {
    this.groupingsLoading = true;
    this.groupingsError = false;
    this.jobDictionaryService.get('', 'name', 'asc', WORKSPACE_PAGE, WORKSPACE_PAGE_SIZE, '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.groupings = response?.data ?? [];
          this.filteredGroupingsView = this.buildFilteredGroupings();
          this.groupingsLoading = false;
        },
        error: () => {
          this.groupingsError = true;
          this.groupingsLoading = false;
        }
      });
  }

  onLeftSearchChange(): void {
    this.candidateGroupsView = this.buildCandidateGroups();
    this.mappedGroupsView = this.buildMappedGroups();
  }

  onRightSearchChange(): void {
    this.filteredGroupingsView = this.buildFilteredGroupings();
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
  }

  candidateKey(candidate: { sourceType: JobMappingSourceType; sourceValue: string }): string {
    return `${candidate.sourceType}::${candidate.sourceValue}`;
  }

  sourceTypeLabel(sourceType: JobMappingSourceType): string {
    switch (sourceType) {
      case JobMappingSourceType.HrisJobCode: return 'HRIS Job Code';
      case JobMappingSourceType.EmployeeJobTitle: return 'Employee Job Title';
      case JobMappingSourceType.EmployeeJobCode: return 'Employee Job Code';
      default: return '';
    }
  }

  /**
   * Composed label for a candidate/mapped row: the source code plus its human-readable
   * name when the BE resolved a distinct one (SourceNameFieldType configured).
   * Renders the value once — never duplicated — when there is no distinct name:
   *   - the name is null/blank (unconfigured, or the code-only HrisPosition path), or
   *   - the name equals the code, case- and whitespace-insensitively.
   * The code/name pattern is a translatable string, not a hardcoded literal.
   */
  sourceDisplay(row: { sourceValue: string; sourceName?: string }): string {
    const code = (row.sourceValue ?? '').trim();
    const name = (row.sourceName ?? '').trim();
    if (!name || name.toLowerCase() === code.toLowerCase()) {
      return code;
    }
    return this.translateService.getDynamicTranslation('{{code}} ({{name}})', { code, name });
  }

  get unmappedCount(): number {
    return this.candidates.length;
  }

  get mappedCount(): number {
    return this.mappings.length;
  }

  get totalCount(): number {
    return this.unmappedCount + this.mappedCount;
  }

  get progressPercent(): number {
    return this.totalCount === 0 ? 0 : Math.round((this.mappedCount / this.totalCount) * 100);
  }

  get selectedCount(): number {
    return this.selectedKeys.size;
  }

  private buildCandidateGroups(): CandidateGroup[] {
    const search = this.leftSearch.trim().toLowerCase();
    const filtered = search
      ? this.candidates.filter(c => c.sourceValue?.toLowerCase().includes(search))
      : this.candidates;
    return this.groupBySourceType(filtered, (items, sourceType) => ({
      sourceType,
      label: this.sourceTypeLabel(sourceType),
      items
    }));
  }

  private buildMappedGroups(): MappedGroup[] {
    const search = this.leftSearch.trim().toLowerCase();
    const filtered = search
      ? this.mappings.filter(m => m.sourceValue?.toLowerCase().includes(search))
      : this.mappings;
    return this.groupBySourceType(filtered, (items, sourceType) => ({
      sourceType,
      label: this.sourceTypeLabel(sourceType),
      items
    }));
  }

  private buildFilteredGroupings(): JobDictionary[] {
    const search = this.rightSearch.trim().toLowerCase();
    if (!search) {
      return this.groupings;
    }
    return this.groupings.filter(g =>
      g.name?.toLowerCase().includes(search) || g.code?.toLowerCase().includes(search));
  }

  private buildMappingCountMap(): void {
    const map = new Map<string, number>();
    for (const m of this.mappings) {
      map.set(m.jobDictionaryId, (map.get(m.jobDictionaryId) ?? 0) + 1);
    }
    this.mappingCountMap = map;
  }

  mappingCountFor(groupingId: string): number {
    return this.mappingCountMap.get(groupingId) ?? 0;
  }

  isSelected(candidate: JobMappingCandidate): boolean {
    return this.selectedKeys.has(this.candidateKey(candidate));
  }

  toggleSelect(candidate: JobMappingCandidate): void {
    const next = new Set(this.selectedKeys);
    const key = this.candidateKey(candidate);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.selection$.next(next);
  }

  clearSelection(): void {
    this.selection$.next(new Set<string>());
  }

  private get selectedCandidates(): JobMappingCandidate[] {
    return this.candidates.filter(c => this.selectedKeys.has(this.candidateKey(c)));
  }

  assignTo(grouping: JobDictionary): void {
    const selected = this.selectedCandidates;
    if (selected.length === 0) {
      return;
    }
    if (selected.length > MAX_BULK_ASSIGN_CANDIDATES) {
      this.toastService.showError(
        this.translateService.getDynamicTranslation(
          'Selection exceeds the {{max}}-item limit. Please reduce your selection and try again.',
          { max: MAX_BULK_ASSIGN_CANDIDATES }
        )
      );
      return;
    }
    const request: BulkAssignMappingRequest = {
      jobDictionaryId: grouping.id,
      candidates: selected.map(c => ({
        sourceType: c.sourceType,
        sourceValue: c.sourceValue
      }))
    };
    this.mappingService.bulkAssign(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showAssignSummary(result);
          this.clearSelection();
          this.loadCandidates();
          this.loadMappings();
        },
        error: (error) => this.toastService.showError(error)
      });
  }

  newGrouping(): void {
    const dialogRef = this.dialog.open(JobDictionaryDialogComponent, {
      data: {},
      width: '480px'
    });
    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((saved) => {
        if (saved) {
          this.loadGroupings();
        }
      });
  }

  changeMapping(mapping: JobDictionaryMapping, grouping: JobDictionary): void {
    if (mapping.jobDictionaryId === grouping.id) {
      return;
    }
    const message = this.translateService.getDynamicTranslation(
      'Re-mapping this value to a different grouping does not reclassify already-captured samples (their grouping is frozen). Only future samples and rollups are affected.'
    );
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.getDynamicTranslation('Change Mapping'),
        message,
        primaryButtonText: this.translateService.getDynamicTranslation('Change Mapping'),
        secondaryButtonText: this.translateService.getDynamicTranslation('Cancel'),
        successMessage: this.translateService.getDynamicTranslation('Mapping updated'),
        errorMessage: this.translateService.getDynamicTranslation('Failed to update mapping'),
        primaryCallback: () =>
          this.mappingService.updateMapping({ ...mapping, jobDictionaryId: grouping.id })
      },
      width: '480px',
      autoFocus: false
    }).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.loadMappings();
        }
      });
  }

  removeMapping(mapping: JobDictionaryMapping): void {
    const message = this.translateService.getDynamicTranslation(
      'Removing this mapping does not reclassify already-captured samples (their grouping is frozen). Only future samples and rollups are affected.'
    );
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.getDynamicTranslation('Remove Mapping'),
        message,
        primaryButtonText: this.translateService.getDynamicTranslation('Remove Mapping'),
        secondaryButtonText: this.translateService.getDynamicTranslation('Cancel'),
        successMessage: this.translateService.getDynamicTranslation('Mapping removed'),
        errorMessage: this.translateService.getDynamicTranslation('Failed to remove mapping'),
        primaryCallback: () => this.mappingService.deleteMapping(mapping.id)
      },
      width: '480px',
      autoFocus: false
    }).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.loadCandidates();
          this.loadMappings();
        }
      });
  }

  groupingName(groupingId: string): string {
    return this.groupings.find(g => g.id === groupingId)?.name ?? '-';
  }

  trackByCandidate = (_: number, candidate: JobMappingCandidate): string => this.candidateKey(candidate);
  trackByMapping = (_: number, mapping: JobDictionaryMapping): string => mapping.id;
  trackByGrouping = (_: number, grouping: JobDictionary): string => grouping.id;

  private showAssignSummary(result: BulkAssignResultItem[]): void {
    const mapped = result.filter(r => r.outcome === BulkAssignOutcome.Mapped).length;
    const skipped = result.filter(r => r.outcome === BulkAssignOutcome.SkippedDuplicate).length;
    const summary = this.translateService.getDynamicTranslation(
      '{{mapped}} mapped, {{skipped}} already mapped',
      { mapped, skipped }
    );
    this.toastService.showSuccess(summary);
  }

  private groupBySourceType<TItem extends { sourceType: JobMappingSourceType }, TGroup>(
    items: TItem[],
    build: (items: TItem[], sourceType: JobMappingSourceType) => TGroup
  ): TGroup[] {
    const map = new Map<JobMappingSourceType, TItem[]>();
    for (const item of items) {
      const bucket = map.get(item.sourceType) ?? [];
      bucket.push(item);
      map.set(item.sourceType, bucket);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([sourceType, groupItems]) => build(groupItems, sourceType));
  }
}
